import {
  friendlySampleSubType,
  friendlySampleType,
  getParentType,
  isWaterType,
  SampleType,
  type SampleSubType,
  zSampleSubType,
  zSampleType,
} from '@resistapp/common/sampling-point-types';
import { z } from 'zod';

export enum ReceivedMaterial {
  ENVIRONMENTAL = 'ENVIRONMENTAL',
  DNA = 'DNA',
}

export const zReceivedMaterial = z.nativeEnum(ReceivedMaterial);

export enum LabWorkbookService {
  SAMPLE_QC = 'SAMPLE_QC',
  DNA_EXTRACTION = 'DNA_EXTRACTION',
  DNA_DILUTION = 'DNA_DILUTION',
}

export const zLabWorkbookService = z.nativeEnum(LabWorkbookService);

export const labWorkbookServiceOrder: readonly LabWorkbookService[] = Object.freeze([
  LabWorkbookService.SAMPLE_QC,
  LabWorkbookService.DNA_EXTRACTION,
  LabWorkbookService.DNA_DILUTION,
]);

export const zLabWorkbookServices = z
  .array(zLabWorkbookService)
  .transform(services => normalizeLabWorkbookServices(services) ?? []);

export const labWorkbookServiceChoices = Object.freeze([
  {
    value: 'dna-dilution-qc',
    label: 'DNA dilution and QC',
    services: [LabWorkbookService.SAMPLE_QC, LabWorkbookService.DNA_DILUTION],
  },
  {
    value: 'dna-extraction-qc',
    label: 'DNA extraction and QC',
    services: [LabWorkbookService.SAMPLE_QC, LabWorkbookService.DNA_EXTRACTION],
  },
  {
    value: 'dna-qc',
    label: 'DNA QC',
    services: [LabWorkbookService.SAMPLE_QC],
  },
] as const);

export function normalizeLabWorkbookServices(
  services: readonly LabWorkbookService[] | null | undefined,
): LabWorkbookService[] | null {
  if (!services || services.length === 0) {
    return null;
  }
  const serviceSet = new Set(services);
  if (serviceSet.size === 0) {
    return null;
  }
  serviceSet.add(LabWorkbookService.SAMPLE_QC);
  return labWorkbookServiceOrder.filter(service => serviceSet.has(service));
}

export function labWorkbookServicesContain(
  services: readonly LabWorkbookService[] | null | undefined,
  service: LabWorkbookService,
): boolean {
  return Boolean(services?.includes(service));
}

export function labWorkbookServicesAllowMissingPrimaryDnaDetails(
  services: readonly LabWorkbookService[] | null | undefined,
): boolean {
  return (
    labWorkbookServicesContain(services, LabWorkbookService.DNA_EXTRACTION) ||
    labWorkbookServicesContain(services, LabWorkbookService.DNA_DILUTION)
  );
}

export function labWorkbookServicesRequirePrimaryElutedDnaVolume(
  services: readonly LabWorkbookService[] | null | undefined,
): boolean {
  return (
    labWorkbookServicesContain(services, LabWorkbookService.DNA_DILUTION) &&
    !labWorkbookServicesContain(services, LabWorkbookService.DNA_EXTRACTION)
  );
}

function formatLocalIsoDate(value: Date): string | null {
  if (Number.isNaN(value.getTime())) {
    return null;
  }
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const zIsoDateString = z.preprocess(
  value => (value instanceof Date ? formatLocalIsoDate(value) : value),
  z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
);
const zIsoDateTimeString = z.preprocess(
  value => (value instanceof Date ? value.toISOString() : value),
  z.string().datetime({ offset: true }),
);

type AcceptedRange =
  | {
      kind: 'between';
      min: number;
      max: number;
      bounds?: '[]' | '()' | '[)' | '(]';
      preferred?: [number, number];
    }
  | {
      kind: 'gte' | 'gt';
      min: number;
      preferred?: [number, number];
    }
  | {
      kind: 'lte' | 'lt';
      max: number;
      preferred?: [number, number];
    };

export type LabDnaStepColumnGroup = 'identity' | 'context' | 'extraction' | 'qa' | 'dilution';
export type LabDnaStepFieldKind = 'text' | 'number' | 'date' | 'sampleType' | 'sampleSubType';

const zLabDnaStepEditableFields = z.object({
  sampleNote: z.string().nullable(),
  arrivalDate: zIsoDateString.nullable(),
  extractionBatch: z.string().nullable(),
  extractionDate: zIsoDateString.nullable(),
  extractionKit: z.string().nullable(),
  extractionLot: z.string().nullable(),
  extractionFilterPortion: z.number().nullable(),
  extractionFilteredVolumeMl: z.number().nullable(),
  extractionSampleWeightMg: z.number().nullable(),
  extractionAddedWaterUl: z.number().nullable(),
  extractionElutionVolumeUl: z.number().nullable(),
  extractionInitials: z.string().nullable(),
  extractionNote: z.string().nullable(),
  qaDate: zIsoDateString.nullable(),
  qaInitials: z.string().nullable(),
  qaMeasuredVolumeUl: z.number().nullable(),
  qaConcentrationNgUl: z.number().nullable(),
  qaQuality260280: z.number().nullable(),
  qaQuality260230: z.number().nullable(),
  qaFluorometricConcentrationNgUl: z.number().nullable(),
  qaNote: z.string().nullable(),
  dilutionDate: zIsoDateString.nullable(),
  dilutionInitials: z.string().nullable(),
  dilutionDesiredFinalVolumeUl: z.number().nullable(),
  dilutionSampleVolumeUl: z.number().nullable(),
  dilutionWaterAddedUl: z.number().nullable(),
  dilutionConcentrationNgUl: z.number().nullable(),
  dilutionQuality260280: z.number().nullable(),
  dilutionQuality260230: z.number().nullable(),
  dilutionNote: z.string().nullable(),
});

export type LabDnaStepEditableFields = z.infer<typeof zLabDnaStepEditableFields>;
export type LabDnaStepEditableFieldKey = keyof LabDnaStepEditableFields;

export const zLabDnaStepRow = z.object({
  sampleId: z.number().int().positive(),
  sampleNumber: z.string().nullable(),
  sampleDescription: z.string().nullable(),
  sampleType: zSampleType.nullable(),
  detailedSampleType: zSampleSubType.nullable(),
  sampleCollectionDate: zIsoDateString.nullable(),
  country: z.string().nullable(),
  filteredVolume: z.number().nullable(),
  baseSampleUpdatedAt: zIsoDateTimeString,
  sampleEditable: z.boolean(),
  primaryDnaDetailsComplete: z.boolean(),
  ...zLabDnaStepEditableFields.shape,
});
export type LabDnaStepRow = z.infer<typeof zLabDnaStepRow>;
export type LabDnaStepDisplayFieldKey = Exclude<
  keyof LabDnaStepRow,
  'sampleId' | 'baseSampleUpdatedAt' | 'sampleEditable' | 'primaryDnaDetailsComplete'
>;

export const zLabDnaStepRowUpdate = z.object({
  sampleId: z.number().int().positive(),
  baseSampleUpdatedAt: zIsoDateTimeString,
  ...zLabDnaStepEditableFields.shape,
});
export type LabDnaStepRowUpdate = z.infer<typeof zLabDnaStepRowUpdate>;

export const zLabDnaStepSummaryResponse = z.object({
  workbookId: z.string().uuid(),
  services: zLabWorkbookServices.nullable(),
  rows: z.array(zLabDnaStepRow),
});
export type LabDnaStepSummaryResponse = z.infer<typeof zLabDnaStepSummaryResponse>;

export const zSaveLabDnaStepRequest = z.object({
  services: zLabWorkbookServices.nullable().optional(),
  rows: z.array(zLabDnaStepRowUpdate),
});
export type SaveLabDnaStepRequest = z.infer<typeof zSaveLabDnaStepRequest>;

export const zMarkLabDnaStepReadyRequest = z.object({});
export type MarkLabDnaStepReadyRequest = z.infer<typeof zMarkLabDnaStepReadyRequest>;

export const zSaveLabDnaStepResponse = zLabDnaStepSummaryResponse;
export type SaveLabDnaStepResponse = z.infer<typeof zSaveLabDnaStepResponse>;

const nanoDropRange: AcceptedRange = {
  kind: 'between',
  min: 1.7,
  max: 2.1,
  bounds: '[]',
  preferred: [1.8, 2.0],
};

const nanoDrop230Range: AcceptedRange = {
  kind: 'between',
  min: 0,
  max: 10,
};

const dilutionConcentrationRange: AcceptedRange = {
  kind: 'between',
  min: 8,
  max: 15,
  bounds: '()',
};

const dilutionTargetConcentrationNgUl = 10;
// Per the DNA dilution protocol, concentrations of 7-15 ng/uL are acceptable as-is; only samples over 15 are diluted.
export const labDnaStepMaxConcentrationWithoutDilutionNgUl = 15;

const filterPortionRange: AcceptedRange = {
  kind: 'between',
  min: 0.25,
  max: 1,
  bounds: '[]',
};

export type LabDnaStepColumnMeta = {
  header: string;
  group: LabDnaStepColumnGroup;
  kind: LabDnaStepFieldKind;
  editable: boolean;
  width?: number;
  pinned?: 'left';
  tooltip?: string | null;
  acceptedRange?: AcceptedRange;
  showRangeHint?: boolean;
};

export const labDnaStepIdentityFieldKeys = [
  'sampleNumber',
  'sampleDescription',
  'sampleType',
  'detailedSampleType',
] as const;

export const labDnaStepContextFieldKeys = ['sampleCollectionDate', 'country', 'filteredVolume'] as const;

export const labDnaStepExtractionFieldKeys = [
  'sampleNote',
  'arrivalDate',
  'extractionBatch',
  'extractionDate',
  'extractionKit',
  'extractionLot',
  'extractionFilterPortion',
  'extractionFilteredVolumeMl',
  'extractionSampleWeightMg',
  'extractionAddedWaterUl',
  'extractionElutionVolumeUl',
  'extractionInitials',
  'extractionNote',
] as const;

export const labDnaStepQaFieldKeys = [
  'qaMeasuredVolumeUl',
  'qaConcentrationNgUl',
  'qaQuality260280',
  'qaQuality260230',
  'qaInitials',
  'qaDate',
  'qaNote',
] as const;

export const labDnaStepDilutionFieldKeys = [
  'qaFluorometricConcentrationNgUl',
  'dilutionDesiredFinalVolumeUl',
  'dilutionSampleVolumeUl',
  'dilutionWaterAddedUl',
  'dilutionConcentrationNgUl',
  'dilutionQuality260280',
  'dilutionQuality260230',
  'dilutionInitials',
  'dilutionDate',
  'dilutionNote',
] as const;

const labDnaStepDilutionCalculationFieldKeys = [
  'dilutionDesiredFinalVolumeUl',
  'dilutionSampleVolumeUl',
  'dilutionWaterAddedUl',
] as const satisfies ReadonlyArray<LabDnaStepEditableFieldKey>;

const labDnaStepDilutionResultFieldKeys = [
  'dilutionConcentrationNgUl',
  'dilutionQuality260280',
  'dilutionQuality260230',
  'dilutionInitials',
  'dilutionDate',
  'dilutionNote',
] as const satisfies ReadonlyArray<LabDnaStepEditableFieldKey>;

export const labDnaStepEditableFieldKeys = [
  ...labDnaStepExtractionFieldKeys,
  ...labDnaStepQaFieldKeys,
  ...labDnaStepDilutionFieldKeys,
] satisfies ReadonlyArray<LabDnaStepEditableFieldKey>;

export const labDnaStepDerivedFieldKeys = ['dilutionSampleVolumeUl', 'dilutionWaterAddedUl'] as const;

const labDnaStepCustomerHiddenFieldKeys = new Set<LabDnaStepDisplayFieldKey>([
  'sampleNote',
  'extractionInitials',
  'extractionNote',
  'qaInitials',
  'qaNote',
  'dilutionInitials',
  'dilutionNote',
]);

export const labDnaStepColumnMetadata: Record<LabDnaStepDisplayFieldKey, LabDnaStepColumnMeta> = {
  sampleNumber: {
    header: 'Sample number',
    group: 'identity',
    kind: 'text',
    editable: false,
    pinned: 'left',
    width: 130,
  },
  sampleDescription: {
    header: 'Sample description',
    group: 'identity',
    kind: 'text',
    editable: false,
    pinned: 'left',
    width: 220,
  },
  sampleType: {
    header: 'Sample type',
    group: 'identity',
    kind: 'sampleType',
    editable: false,
    pinned: 'left',
    width: 150,
  },
  detailedSampleType: {
    header: 'Detailed sample type',
    group: 'identity',
    kind: 'sampleSubType',
    editable: false,
    pinned: 'left',
    width: 200,
  },
  sampleCollectionDate: {
    header: 'Sample collection date',
    group: 'context',
    kind: 'date',
    editable: false,
    width: 150,
  },
  country: {
    header: 'Country',
    group: 'context',
    kind: 'text',
    editable: false,
    width: 110,
  },
  filteredVolume: {
    header: 'Filtered volume (mL)',
    group: 'context',
    kind: 'number',
    editable: false,
    width: 150,
  },
  sampleNote: {
    header: 'Sample note',
    group: 'extraction',
    kind: 'text',
    editable: true,
    width: 220,
  },
  arrivalDate: {
    header: 'Arrival date',
    group: 'extraction',
    kind: 'date',
    editable: true,
    width: 140,
  },
  extractionBatch: {
    header: 'Extraction batch',
    group: 'extraction',
    kind: 'text',
    editable: true,
    width: 140,
  },
  extractionDate: {
    header: 'Extraction date',
    group: 'extraction',
    kind: 'date',
    editable: true,
    width: 140,
  },
  extractionKit: {
    header: 'QIAGEN extraction kit',
    group: 'extraction',
    kind: 'text',
    editable: true,
    width: 220,
    tooltip: 'Kit or method used for the auxiliary DNA extraction step.',
  },
  extractionLot: {
    header: 'Extraction lot',
    group: 'extraction',
    kind: 'text',
    editable: true,
    width: 150,
  },
  extractionFilterPortion: {
    header: 'Filter portion used',
    group: 'extraction',
    kind: 'number',
    editable: true,
    width: 140,
    acceptedRange: filterPortionRange,
    tooltip: 'Fraction of the original filter used for extraction, typically between 0.25 and 1.00.',
  },
  extractionFilteredVolumeMl: {
    header: 'Proportional filtered volume (mL)',
    group: 'extraction',
    kind: 'number',
    editable: true,
    width: 190,
    tooltip: 'Filtered sample volume corresponding to the filter portion used for extraction.',
  },
  extractionSampleWeightMg: {
    header: 'Sample weight (mg)',
    group: 'extraction',
    kind: 'number',
    editable: true,
    width: 140,
  },
  extractionAddedWaterUl: {
    header: 'Added water (uL)',
    group: 'extraction',
    kind: 'number',
    editable: true,
    width: 130,
  },
  extractionElutionVolumeUl: {
    header: 'Elution volume (uL)',
    group: 'extraction',
    kind: 'number',
    editable: true,
    width: 140,
  },
  extractionInitials: {
    header: 'Extraction initials',
    group: 'extraction',
    kind: 'text',
    editable: true,
    width: 130,
  },
  extractionNote: {
    header: 'Extraction note',
    group: 'extraction',
    kind: 'text',
    editable: true,
    width: 220,
  },
  qaMeasuredVolumeUl: {
    header: 'Measured volume (uL)',
    group: 'qa',
    kind: 'number',
    editable: true,
    width: 150,
    tooltip: 'DNA sample volume measured during sample quality control.',
  },
  qaConcentrationNgUl: {
    header: 'Measured DNA concentration (ng/uL)',
    group: 'qa',
    kind: 'number',
    editable: true,
    width: 170,
  },
  qaQuality260280: {
    header: 'Measured 260/280',
    group: 'qa',
    kind: 'number',
    editable: true,
    width: 140,
    acceptedRange: nanoDropRange,
  },
  qaQuality260230: {
    header: 'Measured 260/230',
    group: 'qa',
    kind: 'number',
    editable: true,
    width: 140,
    acceptedRange: nanoDrop230Range,
    showRangeHint: false,
  },
  qaFluorometricConcentrationNgUl: {
    header: 'Fluorometric DNA quantification (ng/uL)',
    group: 'dilution',
    kind: 'number',
    editable: true,
    width: 210,
    tooltip: 'Performed only for dilution service or separately paid fluorometric quantification.',
  },
  qaInitials: {
    header: 'QA initials',
    group: 'qa',
    kind: 'text',
    editable: true,
    width: 120,
  },
  qaDate: {
    header: 'QA date',
    group: 'qa',
    kind: 'date',
    editable: true,
    width: 130,
  },
  qaNote: {
    header: 'QA note',
    group: 'qa',
    kind: 'text',
    editable: true,
    width: 220,
  },
  dilutionDesiredFinalVolumeUl: {
    header: 'Desired final volume (uL)',
    group: 'dilution',
    kind: 'number',
    editable: true,
    width: 150,
  },
  dilutionSampleVolumeUl: {
    header: 'Sample volume (uL)',
    group: 'dilution',
    kind: 'number',
    editable: false,
    width: 140,
    tooltip:
      'Calculated from the QA concentration using c1v1 = c2v2 with a fixed 10 ng/uL target when measured DNA concentration is over 15 ng/uL.',
  },
  dilutionWaterAddedUl: {
    header: 'Water added (uL)',
    group: 'dilution',
    kind: 'number',
    editable: false,
    width: 140,
    tooltip: 'Calculated as desired final volume minus the required sample volume.',
  },
  dilutionConcentrationNgUl: {
    header: 'Post-dilution concentration (ng/uL)',
    group: 'dilution',
    kind: 'number',
    editable: true,
    width: 170,
    acceptedRange: dilutionConcentrationRange,
  },
  dilutionQuality260280: {
    header: 'Post-dilution 260/280',
    group: 'dilution',
    kind: 'number',
    editable: true,
    width: 150,
    acceptedRange: nanoDropRange,
  },
  dilutionQuality260230: {
    header: 'Post-dilution 260/230',
    group: 'dilution',
    kind: 'number',
    editable: true,
    width: 150,
    acceptedRange: nanoDrop230Range,
    showRangeHint: false,
  },
  dilutionInitials: {
    header: 'Dilution initials',
    group: 'dilution',
    kind: 'text',
    editable: true,
    width: 130,
  },
  dilutionDate: {
    header: 'Dilution date',
    group: 'dilution',
    kind: 'date',
    editable: true,
    width: 130,
  },
  dilutionNote: {
    header: 'Dilution note',
    group: 'dilution',
    kind: 'text',
    editable: true,
    width: 220,
  },
};

export function formatLabDnaStepSampleType(type: SampleType | null): string {
  return type ? friendlySampleType(type) : '';
}

export function formatLabDnaStepSampleSubType(type: SampleType | null, subType: SampleSubType | null): string {
  return type && subType ? friendlySampleSubType(subType, getParentType(subType)) : '';
}

export function isLabDnaStepFieldEnabledForServices(
  fieldKey: LabDnaStepDisplayFieldKey,
  services: readonly LabWorkbookService[] | null | undefined,
): boolean {
  const group = labDnaStepColumnMetadata[fieldKey].group;
  if (group === 'identity' || group === 'context' || group === 'qa') {
    return true;
  }
  const hasExtraction = labWorkbookServicesContain(services, LabWorkbookService.DNA_EXTRACTION);
  if (group === 'extraction') {
    return hasExtraction;
  }
  return hasExtraction || labWorkbookServicesContain(services, LabWorkbookService.DNA_DILUTION);
}

export function isLabDnaStepFieldVisibleToCustomer(
  fieldKey: LabDnaStepDisplayFieldKey,
  services: readonly LabWorkbookService[] | null | undefined,
): boolean {
  if (labDnaStepCustomerHiddenFieldKeys.has(fieldKey)) {
    return false;
  }
  return isLabDnaStepFieldEnabledForServices(fieldKey, services);
}

export function normalizeNullableText(value: unknown): string | null {
  if (value == null) {
    return null;
  }
  const trimmed = String(value).trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function normalizeNullableNumber(value: unknown): number | null {
  if (value == null || value === '') {
    return null;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  const parsed = Number(String(value).trim().replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
}

export function normalizeNullableDateString(value: unknown): string | null {
  if (value == null || value === '') {
    return null;
  }
  if (value instanceof Date) {
    return formatLocalIsoDate(value);
  }
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return zIsoDateString.safeParse(trimmed).success ? trimmed : null;
}

export function calculateDilutionVolumes(
  qaConcentrationNgUl: number | null | undefined,
  desiredFinalVolumeUl: number | null | undefined,
): { sampleVolumeUl: number | null; waterAddedUl: number | null } {
  const concentration = normalizeNullableNumber(qaConcentrationNgUl);
  const finalVolume = normalizeNullableNumber(desiredFinalVolumeUl);
  if (concentration == null || finalVolume == null) {
    return { sampleVolumeUl: null, waterAddedUl: null };
  }
  if (concentration <= 0 || finalVolume <= 0) {
    return { sampleVolumeUl: null, waterAddedUl: null };
  }
  if (concentration <= labDnaStepMaxConcentrationWithoutDilutionNgUl) {
    return { sampleVolumeUl: null, waterAddedUl: null };
  }
  const sampleVolumeUl = Number(((dilutionTargetConcentrationNgUl * finalVolume) / concentration).toFixed(1));
  const waterAddedUl = Number((finalVolume - sampleVolumeUl).toFixed(1));
  return {
    sampleVolumeUl,
    waterAddedUl,
  };
}

/**
 * Planned post-dilution concentration implied by the stored dilution calculation (c1v1 = c2v2). Used when the lab
 * skips the optional post-dilution spot check, so downstream normalisation still knows the analysed DNA was diluted.
 */
export function calculateNominalDilutedConcentration(
  qaConcentrationNgUl: number | null | undefined,
  dilutionSampleVolumeUl: number | null | undefined,
  dilutionDesiredFinalVolumeUl: number | null | undefined,
): number | null {
  const concentration = normalizeNullableNumber(qaConcentrationNgUl);
  const sampleVolume = normalizeNullableNumber(dilutionSampleVolumeUl);
  const finalVolume = normalizeNullableNumber(dilutionDesiredFinalVolumeUl);
  if (concentration == null || sampleVolume == null || finalVolume == null) {
    return null;
  }
  if (concentration <= labDnaStepMaxConcentrationWithoutDilutionNgUl) {
    return null;
  }
  if (sampleVolume <= 0 || finalVolume <= 0 || sampleVolume > finalVolume) {
    return null;
  }
  return (concentration * sampleVolume) / finalVolume;
}

type LabDnaStepDilutionFieldCarrier = Pick<
  LabDnaStepEditableFields,
  'qaConcentrationNgUl' | 'dilutionDesiredFinalVolumeUl' | 'dilutionSampleVolumeUl' | 'dilutionWaterAddedUl'
>;

export function applyDerivedLabDnaStepDilutionFields<T extends LabDnaStepDilutionFieldCarrier>(fields: T): T {
  const derived = calculateDilutionVolumes(fields.qaConcentrationNgUl, fields.dilutionDesiredFinalVolumeUl);
  return {
    ...fields,
    dilutionSampleVolumeUl: derived.sampleVolumeUl,
    dilutionWaterAddedUl: derived.waterAddedUl,
  };
}

export function hasStaleDerivedLabDnaStepDilutionVolumes(
  fields: Pick<LabDnaStepEditableFields, 'qaConcentrationNgUl' | 'dilutionSampleVolumeUl' | 'dilutionWaterAddedUl'>,
): boolean {
  const qaConcentration = normalizeNullableNumber(fields.qaConcentrationNgUl);
  if (qaConcentration == null || qaConcentration > labDnaStepMaxConcentrationWithoutDilutionNgUl) {
    return false;
  }
  return (
    normalizeNullableNumber(fields.dilutionSampleVolumeUl) != null ||
    normalizeNullableNumber(fields.dilutionWaterAddedUl) != null
  );
}

type LabDnaStepValidationIssues = Partial<Record<LabDnaStepEditableFieldKey, string[]>>;

function addIssue(issues: LabDnaStepValidationIssues, field: LabDnaStepEditableFieldKey, message: string): void {
  const existing = issues[field] ?? [];
  if (!existing.includes(message)) {
    issues[field] = [...existing, message];
  }
}

function hasValue(value: unknown): boolean {
  if (value == null) {
    return false;
  }
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  return true;
}

function isWaterLikeSampleType(sampleType: SampleType | null | undefined): boolean {
  return sampleType != null && isWaterType(sampleType);
}

function rowNeedsAuxiliaryDnaDetails(
  row: LabDnaStepRow | LabDnaStepRowUpdate,
  services: readonly LabWorkbookService[] | null,
): boolean {
  const sampleType = 'sampleType' in row ? row.sampleType : null;
  return (
    sampleType !== SampleType.CONTROL &&
    labWorkbookServicesAllowMissingPrimaryDnaDetails(services) &&
    ('primaryDnaDetailsComplete' in row ? !row.primaryDnaDetailsComplete : false)
  );
}

const requiredAuxiliaryQaDetailFields = [
  [
    'qaConcentrationNgUl',
    'Enter the QA concentration before marking DNA QC ready because sample-spec DNA details are empty.',
  ],
  [
    'qaQuality260280',
    'Enter the QA 260/280 ratio before marking DNA QC ready because sample-spec DNA details are empty.',
  ],
  [
    'qaQuality260230',
    'Enter the QA 260/230 ratio before marking DNA QC ready because sample-spec DNA details are empty.',
  ],
] as const satisfies ReadonlyArray<readonly [LabDnaStepEditableFieldKey, string]>;

export function rowStartsAnyGroup(
  row: LabDnaStepEditableFields,
  fieldKeys: readonly LabDnaStepEditableFieldKey[],
): boolean {
  return fieldKeys.some(fieldKey => hasValue(row[fieldKey]));
}

export function validateLabDnaStepRow(
  row: LabDnaStepRow | LabDnaStepRowUpdate,
  services: readonly LabWorkbookService[] | null,
): LabDnaStepValidationIssues {
  const issues: LabDnaStepValidationIssues = {};
  const sampleType = 'sampleType' in row ? row.sampleType : null;

  if (labWorkbookServicesContain(services, LabWorkbookService.DNA_EXTRACTION) && sampleType !== SampleType.CONTROL) {
    [
      ['extractionBatch', 'Enter the extraction batch before marking the DNA step ready.'],
      ['extractionDate', 'Enter the extraction date before marking the DNA step ready.'],
      ['extractionKit', 'Enter the extraction kit before marking the DNA step ready.'],
      ['extractionLot', 'Enter the extraction lot before marking the DNA step ready.'],
      ['extractionElutionVolumeUl', 'Enter the extraction elution volume before marking the DNA step ready.'],
    ].forEach(([field, message]) => {
      if (!hasValue(row[field as LabDnaStepEditableFieldKey])) {
        addIssue(issues, field as LabDnaStepEditableFieldKey, message);
      }
    });

    if (isWaterLikeSampleType(sampleType ?? null)) {
      if (!hasValue(row.extractionFilterPortion)) {
        addIssue(issues, 'extractionFilterPortion', 'Enter the filter portion used for environmental water samples.');
      }
      if (!hasValue(row.extractionFilteredVolumeMl)) {
        addIssue(
          issues,
          'extractionFilteredVolumeMl',
          'Enter the proportional filtered volume for environmental water samples.',
        );
      }
    } else if (!hasValue(row.extractionSampleWeightMg)) {
      addIssue(
        issues,
        'extractionSampleWeightMg',
        'Enter the extracted sample weight for environmental solid samples.',
      );
    }
  }

  const qaStarted = rowStartsAnyGroup(row, labDnaStepQaFieldKeys);
  const needsAuxiliaryDnaDetails = rowNeedsAuxiliaryDnaDetails(row, services);
  const qaConcentration = normalizeNullableNumber(row.qaConcentrationNgUl);
  const lowConcentrationSkipsDilution =
    qaConcentration != null && qaConcentration <= labDnaStepMaxConcentrationWithoutDilutionNgUl;

  if (needsAuxiliaryDnaDetails) {
    requiredAuxiliaryQaDetailFields.forEach(([field, message]) => {
      if (!hasValue(row[field])) {
        addIssue(issues, field, message);
      }
    });
  }

  const dilutionRequired =
    needsAuxiliaryDnaDetails &&
    qaConcentration != null &&
    qaConcentration > labDnaStepMaxConcentrationWithoutDilutionNgUl;
  const dilutionStarted =
    rowStartsAnyGroup(row, labDnaStepDilutionCalculationFieldKeys) ||
    (needsAuxiliaryDnaDetails && rowStartsAnyGroup(row, labDnaStepDilutionResultFieldKeys));
  const shouldValidateDilution = (dilutionRequired || dilutionStarted) && !lowConcentrationSkipsDilution;
  const desiredFinalVolume = normalizeNullableNumber(row.dilutionDesiredFinalVolumeUl);

  if (shouldValidateDilution && desiredFinalVolume == null) {
    addIssue(
      issues,
      'dilutionDesiredFinalVolumeUl',
      'Enter the desired final volume once dilution-service fields are started.',
    );
  }
  if (shouldValidateDilution && desiredFinalVolume != null && desiredFinalVolume <= 0) {
    addIssue(issues, 'dilutionDesiredFinalVolumeUl', 'Enter a desired final volume greater than zero.');
  }

  if (shouldValidateDilution && !hasValue(row.qaConcentrationNgUl)) {
    addIssue(issues, 'qaConcentrationNgUl', 'Enter the QA concentration before calculating dilution-service volumes.');
  }

  if (qaStarted) {
    if (row.qaQuality260280 != null && row.qaQuality260280 > 10) {
      addIssue(issues, 'qaQuality260280', 'Enter the QA 260/280 ratio as a realistic number.');
    }
    if (row.qaQuality260230 != null && row.qaQuality260230 > 10) {
      addIssue(issues, 'qaQuality260230', 'Enter the QA 260/230 ratio as a realistic number.');
    }
  }

  if (shouldValidateDilution) {
    const derived = calculateDilutionVolumes(qaConcentration, desiredFinalVolume);
    const storedSampleVolume = normalizeNullableNumber(row.dilutionSampleVolumeUl);
    const storedWaterAdded = normalizeNullableNumber(row.dilutionWaterAddedUl);
    if (
      derived.sampleVolumeUl != null &&
      storedSampleVolume != null &&
      Math.abs(derived.sampleVolumeUl - storedSampleVolume) > 0.05
    ) {
      addIssue(
        issues,
        'dilutionSampleVolumeUl',
        'Calculated sample volume is out of sync with the current QA concentration or desired final volume.',
      );
    }
    if (
      derived.waterAddedUl != null &&
      storedWaterAdded != null &&
      Math.abs(derived.waterAddedUl - storedWaterAdded) > 0.05
    ) {
      addIssue(
        issues,
        'dilutionWaterAddedUl',
        'Calculated water volume is out of sync with the current QA concentration or desired final volume.',
      );
    }
  }

  return issues;
}

export function normalizeLabDnaStepEditableFields(fields: Partial<LabDnaStepEditableFields>): LabDnaStepEditableFields {
  return {
    sampleNote: normalizeNullableText(fields.sampleNote),
    arrivalDate: normalizeNullableDateString(fields.arrivalDate),
    extractionBatch: normalizeNullableText(fields.extractionBatch),
    extractionDate: normalizeNullableDateString(fields.extractionDate),
    extractionKit: normalizeNullableText(fields.extractionKit),
    extractionLot: normalizeNullableText(fields.extractionLot),
    extractionFilterPortion: normalizeNullableNumber(fields.extractionFilterPortion),
    extractionFilteredVolumeMl: normalizeNullableNumber(fields.extractionFilteredVolumeMl),
    extractionSampleWeightMg: normalizeNullableNumber(fields.extractionSampleWeightMg),
    extractionAddedWaterUl: normalizeNullableNumber(fields.extractionAddedWaterUl),
    extractionElutionVolumeUl: normalizeNullableNumber(fields.extractionElutionVolumeUl),
    extractionInitials: normalizeNullableText(fields.extractionInitials),
    extractionNote: normalizeNullableText(fields.extractionNote),
    qaDate: normalizeNullableDateString(fields.qaDate),
    qaInitials: normalizeNullableText(fields.qaInitials),
    qaMeasuredVolumeUl: normalizeNullableNumber(fields.qaMeasuredVolumeUl),
    qaConcentrationNgUl: normalizeNullableNumber(fields.qaConcentrationNgUl),
    qaQuality260280: normalizeNullableNumber(fields.qaQuality260280),
    qaQuality260230: normalizeNullableNumber(fields.qaQuality260230),
    qaFluorometricConcentrationNgUl: normalizeNullableNumber(fields.qaFluorometricConcentrationNgUl),
    qaNote: normalizeNullableText(fields.qaNote),
    dilutionDate: normalizeNullableDateString(fields.dilutionDate),
    dilutionInitials: normalizeNullableText(fields.dilutionInitials),
    dilutionDesiredFinalVolumeUl: normalizeNullableNumber(fields.dilutionDesiredFinalVolumeUl),
    dilutionSampleVolumeUl: normalizeNullableNumber(fields.dilutionSampleVolumeUl),
    dilutionWaterAddedUl: normalizeNullableNumber(fields.dilutionWaterAddedUl),
    dilutionConcentrationNgUl: normalizeNullableNumber(fields.dilutionConcentrationNgUl),
    dilutionQuality260280: normalizeNullableNumber(fields.dilutionQuality260280),
    dilutionQuality260230: normalizeNullableNumber(fields.dilutionQuality260230),
    dilutionNote: normalizeNullableText(fields.dilutionNote),
  };
}
